/**
 * MBA Lab — pure state/readout model for the Change and AI Readiness Assessment.
 * No DOM, storage, or analytics dependencies - change-readiness-assessment.js
 * owns all of that and consumes buildReadout() from here.
 *
 * Output is a structured readiness profile - grouped risks, still-open
 * questions, and a prioritized action plan - never a numeric or percentage
 * readiness score. The 12 dimensions below are exactly the plan's list
 * (stakeholders through communication credibility).
 */

import { DIMENSIONS, PRIORITY_ORDER } from './readiness-dimensions.js';

export const SCHEMA_NAME = 'mbalab.change-readiness-assessment';
export const SCHEMA_VERSION = 1;

export const LIMITS = Object.freeze({
  maxSerializedBytes: 10 * 1024,
});

export const STATUS = Object.freeze({
  NOT_STARTED: 'not-started',
  IN_PROGRESS: 'in-progress',
  ADDRESSED: 'addressed',
});

const STATUS_VALUES = Object.values(STATUS);

const DIMENSIONS_BY_KEY = new Map(DIMENSIONS.map((d) => [d.key, d]));
export const TOTAL_DIMENSIONS = DIMENSIONS.length;

export function createInitialState() {
  const statuses = {};
  for (const d of DIMENSIONS) statuses[d.key] = null;
  return { schema: SCHEMA_NAME, version: SCHEMA_VERSION, savedAt: null, statuses };
}

function isValidStatus(v) {
  return STATUS_VALUES.includes(v);
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const rawStatuses = r.statuses && typeof r.statuses === 'object' ? r.statuses : {};
  const statuses = {};
  for (const d of DIMENSIONS) {
    const v = rawStatuses[d.key];
    statuses[d.key] = isValidStatus(v) ? v : null;
  }
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    statuses,
  };
}

const MIGRATIONS = {
  0: (state) => ({ ...state, version: 1 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    // Number.isInteger(NaN) is false, so this also rejects a corrupted NaN
    // version - without this, `NaN > SCHEMA_VERSION` and `MIGRATIONS[NaN]`
    // are both silently false/undefined, so a corrupted version would fall
    // through as if it were already current instead of being flagged.
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

export function setStatus(state, dimensionKey, status) {
  if (!DIMENSIONS_BY_KEY.has(dimensionKey) || !isValidStatus(status)) return state;
  return { ...state, statuses: { ...state.statuses, [dimensionKey]: status } };
}

export function countAnswered(state) {
  return Object.values(state.statuses).filter((v) => v !== null).length;
}

export function isComplete(state) {
  return DIMENSIONS.every((d) => isValidStatus(state.statuses[d.key]));
}

/** @returns {null | {notStarted: object[], inProgress: object[], addressed: object[], actionPlan: object[]}} */
export function buildReadout(state) {
  if (!isComplete(state)) return null;

  const byStatus = { [STATUS.NOT_STARTED]: [], [STATUS.IN_PROGRESS]: [], [STATUS.ADDRESSED]: [] };
  for (const d of DIMENSIONS) {
    const status = state.statuses[d.key];
    const text =
      status === STATUS.NOT_STARTED ? d.riskText : status === STATUS.IN_PROGRESS ? d.progressText : d.addressedText;
    byStatus[status].push({ key: d.key, label: d.label, question: d.question, text });
  }

  // Action plan: up to 3 items, drawn from not-started first (in fixed
  // priority order), then in-progress if fewer than 3 gaps remain - never a
  // score, just a deterministic pick of what to look at first.
  const priorityRank = new Map(PRIORITY_ORDER.map((key, i) => [key, i]));
  const byPriority = (a, b) => priorityRank.get(a.key) - priorityRank.get(b.key);
  const actionPlan = [...byStatus[STATUS.NOT_STARTED]]
    .sort(byPriority)
    .concat([...byStatus[STATUS.IN_PROGRESS]].sort(byPriority))
    .slice(0, 3);

  return {
    notStarted: byStatus[STATUS.NOT_STARTED],
    inProgress: byStatus[STATUS.IN_PROGRESS],
    addressed: byStatus[STATUS.ADDRESSED],
    actionPlan,
  };
}
